import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cognitoAuth } from '../services/cognitoAuth';
import QRCode from 'react-qr-code';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [newPasswordNeeded, setNewPasswordNeeded] = useState<{
    session: string;
  } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [mfaChallenge, setMfaChallenge] = useState<{
    type: 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA' | 'MFA_SETUP';
    session: string;
  } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [pendingUsername, setPendingUsername] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [authMode, setAuthMode] = useState<
    'signin' | 'signup' | 'confirm' | 'forgot' | 'reset'
  >('signin');
  const totpIssuer = useMemo(
    () => process.env.REACT_APP_COGNITO_TOTP_ISSUER || 'Aurora',
    []
  );
  const totpUri = useMemo(() => {
    const totpAccount = pendingUsername || signupEmail || username;
    if (!totpSecret || !totpAccount) return '';
    const label = `${totpIssuer}:${totpAccount}`;
    return `otpauth://totp/${encodeURIComponent(label)}?secret=${encodeURIComponent(
      totpSecret
    )}&issuer=${encodeURIComponent(totpIssuer)}&algorithm=SHA1&digits=6&period=30`;
  }, [totpIssuer, totpSecret, username]);

  useEffect(() => {
    const handled = cognitoAuth.handleRedirectCallback();
    if (handled || localStorage.getItem('cognito_id_token')) {
      navigate('/voice?agent=shell', { replace: true });
    }
  }, [navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      const res = await cognitoAuth.loginWithPassword(username, password);
      if (res && res.challenge === 'NEW_PASSWORD_REQUIRED' && res.session) {
        setNewPasswordNeeded({ session: res.session });
        return;
      }
      if (res && res.challenge && res.session) {
        if (
          res.challenge === 'SMS_MFA' ||
          res.challenge === 'SOFTWARE_TOKEN_MFA' ||
          res.challenge === 'MFA_SETUP'
        ) {
          setMfaChallenge({ type: res.challenge, session: res.session });
          if (res.challenge === 'MFA_SETUP') {
            const setup = await cognitoAuth.startSoftwareTokenSetup(
              res.session
            );
            setTotpSecret(setup.secretCode);
            setMfaChallenge({ type: res.challenge, session: setup.session });
          }
          return;
        }
      }
      navigate('/voice?agent=shell', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const onCompleteNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      const res = await cognitoAuth.completeNewPassword(
        username,
        newPassword,
        newPasswordNeeded!.session,
        fullName ? { name: fullName } : undefined
      );
      if (res && res.challenge && res.session) {
        if (
          res.challenge === 'SMS_MFA' ||
          res.challenge === 'SOFTWARE_TOKEN_MFA' ||
          res.challenge === 'MFA_SETUP'
        ) {
          setMfaChallenge({ type: res.challenge, session: res.session });
          if (res.challenge === 'MFA_SETUP') {
            const setup = await cognitoAuth.startSoftwareTokenSetup(
              res.session
            );
            setTotpSecret(setup.secretCode);
            setMfaChallenge({ type: res.challenge, session: setup.session });
          }
          return;
        }
      }
      navigate('/voice?agent=shell', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Password update failed');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaChallenge) return;
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      if (mfaChallenge.type === 'MFA_SETUP') {
        await cognitoAuth.confirmSoftwareTokenSetup(
          username,
          mfaCode,
          mfaChallenge.session
        );
      } else {
        await cognitoAuth.respondToMfaChallenge(
          username,
          mfaCode,
          mfaChallenge.session,
          mfaChallenge.type
        );
      }
      navigate('/voice?agent=shell', { replace: true });
    } catch (err: any) {
      setError(err?.message || 'MFA verification failed');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      const attrs: Record<string, string> = {
        email: signupEmail,
      };
      if (signupName) {
        attrs.name = signupName;
      }
      const res = await cognitoAuth.signUp(signupEmail, signupPassword, attrs);
      if (res.userConfirmed) {
        setMessage('Account created. Please sign in.');
        setAuthMode('signin');
        return;
      }
      setPendingUsername(signupEmail);
      setAuthMode('confirm');
    } catch (err: any) {
      setError(err?.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      await cognitoAuth.confirmSignUp(
        pendingUsername || signupEmail || username,
        confirmationCode
      );
      setMessage('Account confirmed. Please sign in.');
      setAuthMode('signin');
    } catch (err: any) {
      setError(err?.message || 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const onResendConfirmation = async () => {
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      await cognitoAuth.resendSignUpCode(
        pendingUsername || signupEmail || username
      );
      setMessage('Verification code resent.');
    } catch (err: any) {
      setError(err?.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      await cognitoAuth.forgotPassword(username);
      setMessage('A password reset code was sent to your verified contact.');
      setAuthMode('reset');
    } catch (err: any) {
      setError(err?.message || 'Failed to send password reset code');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    try {
      await cognitoAuth.confirmForgotPassword(
        username,
        resetCode,
        resetPassword
      );
      setMessage('Password updated. You can now sign in.');
      setPassword('');
      setResetCode('');
      setResetPassword('');
      setAuthMode('signin');
    } catch (err: any) {
      setError(err?.message || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="mx-auto max-h-[90vh] w-full max-w-md overflow-y-auto rounded border p-6 text-left shadow-sm">
        <h1 className="mb-4 text-2xl font-semibold text-gray-900">
          Sign in to Aurora
        </h1>
        {error && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {message && (
          <div className="mb-4 rounded border border-green-200 bg-green-50 p-3 text-sm text-green-700">
            {message}
          </div>
        )}
        {authMode === 'signup' ? (
          <form onSubmit={onSubmitSignUp} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm text-gray-700">Email</label>
              <input
                type="email"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                className="w-full rounded border px-3 py-2"
                autoComplete="email"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                Full Name
              </label>
              <input
                value={signupName}
                onChange={(e) => setSignupName(e.target.value)}
                className="w-full rounded border px-3 py-2"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                Password
              </label>
              <input
                type="password"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
                className="w-full rounded border px-3 py-2"
                autoComplete="new-password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? 'Creating...' : 'Create Account'}
            </button>
            <button
              type="button"
              onClick={() => setAuthMode('signin')}
              className="w-full rounded border px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
            >
              Back to Sign In
            </button>
          </form>
        ) : authMode === 'confirm' ? (
          <form onSubmit={onSubmitConfirm} className="space-y-4">
            <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
              Enter the verification code sent to your email.
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                Verification Code
              </label>
              <input
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                className="w-full rounded border px-3 py-2"
                autoComplete="one-time-code"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? 'Confirming...' : 'Confirm Account'}
            </button>
            <button
              type="button"
              onClick={onResendConfirmation}
              disabled={loading}
              className="w-full rounded border px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60"
            >
              Resend Code
            </button>
            <button
              type="button"
              onClick={() => setAuthMode('signin')}
              className="w-full rounded border px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
            >
              Back to Sign In
            </button>
          </form>
        ) : authMode === 'forgot' ? (
          <form onSubmit={onSubmitForgotPassword} className="space-y-4">
            <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
              Enter your username or email and we will send you a password reset
              code.
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                Username or Email
              </label>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full rounded border px-3 py-2"
                autoComplete="username"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
            <button
              type="button"
              onClick={() => setAuthMode('signin')}
              className="w-full rounded border px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
            >
              Back to Sign In
            </button>
          </form>
        ) : authMode === 'reset' ? (
          <form onSubmit={onSubmitResetPassword} className="space-y-4">
            <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
              Enter the verification code and choose a new password.
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                Verification Code
              </label>
              <input
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                className="w-full rounded border px-3 py-2"
                autoComplete="one-time-code"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                New Password
              </label>
              <input
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                className="w-full rounded border px-3 py-2"
                autoComplete="new-password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? 'Updating...' : 'Reset Password'}
            </button>
            <button
              type="button"
              onClick={() => setAuthMode('forgot')}
              className="w-full rounded border px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
            >
              Send Another Code
            </button>
          </form>
        ) : !newPasswordNeeded && !mfaChallenge ? (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                Username
              </label>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full rounded border px-3 py-2"
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded border px-3 py-2"
                autoComplete="current-password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <button
              type="button"
              onClick={() => {
                setError(null);
                setMessage(null);
                setAuthMode('forgot');
              }}
              className="w-full text-sm text-blue-600 hover:text-blue-700"
            >
              Forgot password?
            </button>
            <button
              type="button"
              onClick={() => setAuthMode('signup')}
              className="w-full rounded border px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
            >
              Create an Account
            </button>
          </form>
        ) : newPasswordNeeded ? (
          <form onSubmit={onCompleteNewPassword} className="space-y-4">
            <div className="rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
              New password required. Please set a new password to continue.
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                Full Name
              </label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded border px-3 py-2"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full rounded border px-3 py-2"
                autoComplete="new-password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? 'Updating...' : 'Set New Password'}
            </button>
          </form>
        ) : (
          <form onSubmit={onSubmitMfa} className="space-y-4">
            {mfaChallenge?.type === 'MFA_SETUP' ? (
              <div className="rounded border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
                MFA setup required. Add this secret to your authenticator app
                and enter the 6-digit code.
                {totpSecret && (
                  <div className="mt-2 space-y-3">
                    {totpUri && (
                      <div className="flex justify-center rounded bg-white p-3">
                        <QRCode value={totpUri} size={160} />
                      </div>
                    )}
                    <div className="font-mono break-all rounded bg-white p-2 text-sm">
                      {totpSecret}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                Enter the verification code from your{' '}
                {mfaChallenge?.type === 'SMS_MFA' ? 'SMS' : 'authenticator app'}
                .
              </div>
            )}
            <div>
              <label className="mb-1 block text-sm text-gray-700">
                Verification Code
              </label>
              <input
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                className="w-full rounded border px-3 py-2"
                autoComplete="one-time-code"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
